import { defineComponent as _defineComponent } from 'vue'
import { debounce } from 'lodash-es';
import { computed, inject, onMounted, ref } from 'vue';

import {
  BaseText,
  ButtonPrimary,
  Heading,
  SearchBar,
  Row,
  InitialAvatarIcon,
  HelperViewContextual,
  LoaderIndicator
} from '@warrenbrasil/nebraska-web';
import { NebraskaColors } from '@warrenbrasil/nebraska-tokens-web';

import { IAdvisorList } from '@/modules/onboarding/signup/types';
import { getAdvisorList } from '@/modules/onboarding/signup/services/api';
import {
  trackAdvisorModalView,
  trackFillAdvisorEmail,
  trackSendAdvisorEmail
} from '@/modules/onboarding/signup/helpers/track-events';
import { isBFFError } from '@/modules/common/helpers/bff';
import { notify } from '@/modules/common/helpers/notification';
import { NotificationTypes } from '@/modules/common/services/http/notification';
import {
  IRecaptcha,
  Recaptcha,
  RecaptchaActions
} from '@/modules/authentication/components/Recaptcha';


export default /*#__PURE__*/_defineComponent({
  __name: 'AdvisorStep',
  props: {
  isFetching: { type: Boolean, default: false }
},
  emits: ['close', 'submit'],
  setup(__props, { emit }) {



const searchValue = ref('');
const helperText = ref('');
const loading = ref<boolean>(false);
const selectedAdvisor = ref<IAdvisorList>();
const isFetchingList = ref(false);
const isScrolling = ref(false);
const isEmpty = ref(false);

const pagination = ref({
  pageSize: 15,
  pageIndex: 1,
  totalPages: 10
});

const recaptcha = ref<InstanceType<typeof Recaptcha> | null>(null);
const recaptchaToken = ref<string>('');

const advisorsList = ref<IAdvisorList[]>([]);



const updateUserHaveAdvisor = inject(
  'updateUserHaveAdvisor',
  (value: boolean) => ({
    value
  })
);
const updateAdvisor = inject('updateAdvisor', (value: IAdvisorList) => ({
  value
}));

const helperData = computed(() => {
  return searchValue.value && isEmpty.value
    ? {
        title: `Nenhum resultado encontrado para "${helperText.value}"`,
        description:
          'Verifique se há erros de digitação ou ortografia na sua pesquisa, por favor.',
        icon: 'EE0153'
      }
    : {
        title: 'Buscar assessora ou assessor',
        description:
          'Busque a sua assessora ou assessor de investimentos pelo nome.',
        icon: 'EE0151'
      };
});

const showButton = computed(() => {
  return (
    advisorsList.value.some(item => item.selected) && !isFetchingList.value
  );
});

const onInputChange = debounce(handleSearch, 500);

async function handleSearch(value: string) {
  helperText.value = value;

  await fetchAdvisorList();
  registerObserver();
}

function handleClear() {
  searchValue.value = '';
  helperText.value = '';

  advisorsList.value.splice(0, advisorsList.value.length);
  isEmpty.value = false;
}

function selectOption(value: {
  name: string;
  email: string;
  selected?: boolean;
}) {
  advisorsList.value = advisorsList.value.map(item => {
    return {
      ...item,
      selected: item.email === value.email
    };
  });

  trackFillAdvisorEmail();

  selectedAdvisor.value = value;
}

function submit() {
  updateAdvisor(selectedAdvisor.value!);
  updateUserHaveAdvisor(true);
  emit('submit', selectedAdvisor.value!.email);
  trackSendAdvisorEmail();
}

async function onRecaptchaToken(token: string) {
  recaptchaToken.value = token;
}

async function handleRecaptcha() {
  const recaptchaEl = recaptcha.value as unknown as IRecaptcha;

  try {
    await recaptchaEl.onChallenge(RecaptchaActions.ADVISOR_LIST);
  } catch {
    notify({
      group: 'wrn-notifications',
      text: 'Ocorreu um erro ao continuar, verifique sua internet e tente novamente.',
      type: NotificationTypes.error
    });

    emit('close');
  }
}

async function fetchAdvisorList(
  fetch = true,
  scrolling = false,
  pageIndex = 1
) {
  try {
    isFetchingList.value = fetch;
    isScrolling.value = scrolling;

    if (!searchValue.value) {
      advisorsList.value = [];
      isEmpty.value = false;

      return;
    }

    const data = await getAdvisorList(
      recaptchaToken.value,
      searchValue.value,
      pageIndex
    );

    isEmpty.value = Boolean(
      data.advisorsResponse.length < 1 && searchValue.value
    );

    pagination.value = {
      pageIndex: data.pageIndex,
      pageSize: data.pageSize,
      totalPages: data.totalPages
    };

    const filteredList = data.advisorsResponse.map(advisor => {
      return {
        ...advisor,
        selected: false
      };
    });

    if (isScrolling.value) {
      advisorsList.value.push(...filteredList);
    } else {
      advisorsList.value = filteredList;
    }
  } catch (e) {
    let errorMessage =
      'Não foi possível realizar a operação, por favor tente novamente.';

    if (isBFFError(e)) {
      errorMessage = e.error.message.formatted;
    }

    notify({
      group: 'wrn-notifications',
      text: errorMessage,
      type: NotificationTypes.error
    });

    emit('close');
  } finally {
    isFetchingList.value = false;
    isScrolling.value = false;
  }
}

function observerLastRow(observerContentRow: IntersectionObserver) {
  const rows = document.querySelectorAll('[data-testid="advisor-option"]');

  if (rows.length === 0) return;
  if (pagination.value.pageIndex === pagination.value.totalPages) return;

  const lastRow = rows[rows.length - 1];
  observerContentRow.observe(lastRow);
}

function registerObserver() {
  const observerContentRow = new IntersectionObserver(
    async ([lastRow], observer) => {
      if (!lastRow.isIntersecting) return;

      observer.unobserve(lastRow.target);
      await fetchAdvisorList(false, true, pagination.value.pageIndex + 1);
      observerLastRow(observerContentRow);
    }
  );

  observerLastRow(observerContentRow);
}

onMounted(() => {
  trackAdvisorModalView();

  handleRecaptcha();
});

return { __sfc: true,searchValue, helperText, loading, selectedAdvisor, isFetchingList, isScrolling, isEmpty, pagination, recaptcha, recaptchaToken, advisorsList, emit, updateUserHaveAdvisor, updateAdvisor, helperData, showButton, onInputChange, handleSearch, handleClear, selectOption, submit, onRecaptchaToken, handleRecaptcha, fetchAdvisorList, observerLastRow, registerObserver, BaseText, ButtonPrimary, Heading, SearchBar, Row, InitialAvatarIcon, HelperViewContextual, LoaderIndicator, NebraskaColors, Recaptcha }
}

})